import GeneratingIcon from "@/assets/svg/ic_generating_avatar.svg?react";
import CollaborationStore from "@/store/collaboration.store";
import { formatRecordTime } from "@/utils/formatter";
import { useCallback, useRef, useEffect, useState, useMemo } from "react";
import { debounce } from "@common/utils/common";
import { IPG } from "@/types/summary";
import Tooltip from "@/component/Tooltip";
import DownIcon from "@/assets/common/ic_down.svg?react";
import { IEditRecordParams, IMergeRecordParams } from "@/types/record";
import ContentItem from "../SrcContent/ContentItem";
import { RuningInLink } from '@/constants';
import SimulSpeakerIcon from "@/assets/simulInter/ic_speaker_avatar.svg?react";
interface ScrollableContentProps {
  data: IPG[]; // 数据源，可以是原文或译文
  unstableText?: string; // 实时文本
  isTrans?: boolean; // 是否是译文
  onEditRecord?: (params: IEditRecordParams) => Promise<void>;
  onModifySpeakerName?: (updatedParas: IPG[], editRoleType: "ONE" | "BATCH") => Promise<void>;
  onMergeRecord?: (params: IMergeRecordParams) => Promise<void>;
  onSpeakerPopoverChange?: (visible: boolean) => void;
  className?: string; // 样式自定义
}

interface CursorPosition {
  pi: string;
  pos?: number;
  si: string;
}

const ScrollableContent: React.FC<ScrollableContentProps> = ({
  data,
  unstableText,
  isTrans,
  onEditRecord,
  onModifySpeakerName,
  onMergeRecord,
  onSpeakerPopoverChange,
  className = ""
}) => {
  const getAvatarIndex = CollaborationStore((state) => state.getAvatarIndex);
  const focusedPara = CollaborationStore((state) => state.focusedPara);

  // 滚动相关状态
  const observerRef = useRef<IntersectionObserver | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const container = useRef<HTMLDivElement>(null);
  const [firstScroll, setFirstScroll] = useState(true);
  const autoScroll = useRef(true);
  const contentListRef = useRef<HTMLDivElement>(null);
  const cursorPositionRef = useRef<CursorPosition | null>(null);
  const scrollTimerRef = useRef<NodeJS.Timeout | null>(null);
  // const { isVisitor } = useLoginStore();

  useEffect(() => {
    if (focusedPara) {
      handleStopAutoScroll();
    }
  }, [focusedPara]);

  const handleStopAutoScroll = () => {
    if (autoScroll.current) {
      autoScroll.current = false;
      setShowScrollToBottom(true);
    }
  };

  // 首次滚动到底部
  useEffect(() => {
    if (!firstScroll) return;
    bottomRef.current?.scrollIntoView({
      behavior: "instant",
    });
    data.length && setFirstScroll(false);
  }, [data.length, firstScroll]);

  // IntersectionObserver设置
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting && autoScroll.current) {
          bottomRef.current?.scrollIntoView();
        } else if (entries[0].isIntersecting && !autoScroll.current) {
          autoScroll.current = true;
        }
      },
      {
        root: container.current,
        threshold: 0.7,
      }
    );

    if (bottomRef.current) {
      observerRef.current.observe(bottomRef.current);
    }

    return () => {
      observerRef.current?.disconnect();
    };
  }, []);

  const handleStartObserving = () => {
    if (bottomRef.current) {
      observerRef.current?.observe(bottomRef.current);
    }
  };

  const scrollToBottom = useCallback(() => {
    autoScroll.current = true;
    bottomRef.current?.scrollIntoView();
    setShowScrollToBottom(false);
    handleStartObserving();
  }, []);

  // 处理滚动事件
  const handleScroll = useCallback(() => {
    if (!container.current) return;

    // 立即显示滚动条
    container.current.classList.add('scroll-visible');

    // 清除之前的定时器
    if (scrollTimerRef.current) {
      clearTimeout(scrollTimerRef.current);
    }

    // 1秒后隐藏滚动条
    scrollTimerRef.current = setTimeout(() => {
      if (container.current) {
        container.current.classList.remove('scroll-visible');
      }
    }, 1000);
  }, []);

  const debounceHandleScroll = useCallback(
    debounce(() => {
      if (!container.current) return;
      const { scrollTop, scrollHeight, clientHeight } = container.current;

      // Web端使用原有的判断逻辑
      if (scrollTop >= scrollHeight - clientHeight - 40) {
        setShowScrollToBottom(false);
      } else {
        setShowScrollToBottom(true);
      }
    }, 500),
    []
  );

  const validTcDataList = useMemo(() => {
    return data.filter((item) => item.sc.length > 0 && item.sc.some((sc) => sc.tc !== '' || sc.isEdited === true));
  }, [data]);

  const validTransTcDataList = useMemo(() => {
    return data.filter((item) => item.sc.length > 0 && item.sc.some((sc) => sc.transTc !== '' || sc.isEdited === true));
  }, [data]);

  // 监听 DOM 变化
  useEffect(() => {
    let observer: MutationObserver | null = null;

    observer = new MutationObserver((mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.type === "childList") {
          mutation.removedNodes.forEach((node) => {
            if (
              node.nodeType === Node.ELEMENT_NODE &&
              cursorPositionRef.current &&
              (node as HTMLElement).classList.contains("paragraph-item")
            ) {
              const selection = window.getSelection();
              selection?.removeAllRanges();

              setTimeout(() => {
                const element = document.querySelector(
                  `[data-sent-id="${cursorPositionRef.current?.si}"]`
                ) as HTMLDivElement;

                const textNode = element?.firstChild;
                const range = document.createRange();

                try {
                  if (textNode && textNode.nodeType === Node.TEXT_NODE) {
                    const length = textNode.textContent?.length || 0;
                    range.setStart(textNode, length);
                    range.setEnd(textNode, length);
                  } else if (element) {
                    range.setStart(element, 0);
                    range.setEnd(element, 0);
                  }

                  selection?.addRange(range);
                  cursorPositionRef.current = null;
                } catch (error) {
                  console.error("Error setting cursor position:", error);
                }
              }, 0);
            }
          });
        }
      }
    });

    if (contentListRef.current) {
      observer.observe(contentListRef.current, {
        childList: true,
        subtree: true,
      });
    }

    return () => {
      observer?.disconnect();
      observer = null;
    };
  }, []);

  // 渲染列表项
  const renderItem = useCallback(
    (item: IPG) => {
      return (
        <div key={item.pi} className="mb-6 paragraph-item" id={item.pi}>
          <ContentItem
            record={item}
            speakerName={item.speakerName?.[0]}
            avatar={<SimulSpeakerIcon className="size-6 mr-4" />}
            pi={item.pi}
            editable={false} // 目前产品达成共识，会中加入都不能编辑
            isTrans={isTrans}
            recordTime={formatRecordTime((item.sc[0]?.bt || 0) / 1000)}
            avatarIndex={getAvatarIndex(item.role)}
            useDefaultIcon={true}
            speaking={false}
            onEditRecord={onEditRecord}
            onModifySpeakerName={onModifySpeakerName}
            onSpeakerPopoverChange={onSpeakerPopoverChange}
            onMergeRecord={onMergeRecord}
          />
        </div>
      );
    },
    [
      getAvatarIndex,
      onEditRecord,
      onModifySpeakerName,
      onSpeakerPopoverChange,
      onMergeRecord,
    ]
  );

  // 渲染实时语音项
  const renderUnstableItem = useCallback(() => {
    return (
      <div>
        <ContentItem
          speakerName="发言人"
          speaking
          useDefaultIcon
          editable={false}
          isTrans={isTrans}
          avatar={<GeneratingIcon className="size-6 mr-4" />}
          unstableContent={unstableText}
        />
      </div>
    );
  }, [unstableText]);

  return (
    <div className={`relative ${className}`}>
      <div
        className={`${RuningInLink ? 'max-w-150 mx-auto px-6' : 'px-28.5'} h-full flex-1 overflow-auto scrollable-content`}
        onScroll={() => {
          handleScroll();
          debounceHandleScroll();
        }}
        ref={container}
      >
        <div ref={contentListRef}>
          {isTrans ? validTransTcDataList.map((item) => renderItem(item)) : validTcDataList.map((item) => renderItem(item))}
          {renderUnstableItem()}
        </div>
        <div ref={bottomRef} className="h-1"></div>
      </div>

      {/* 回到当前按钮 */}
      {showScrollToBottom && (
        <Tooltip hover content={"回到当前"}>
          <div
            onClick={scrollToBottom}
            className="absolute bottom-6 right-6 size-10 border-1 border-solid b-color-#ECEEF3 rounded-50% flex justify-center items-center cursor-pointer bg-#fff z-10"
          >
            <DownIcon className="size-4.5" />
          </div>
        </Tooltip>
      )}
    </div>
  );
};

export default ScrollableContent; 